import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import type { Kreisverband, KreisverbandInput } from "../types";
import { esc } from "../utils/html";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";

// Menüpunkt "Kreisverbände" (nur DRK-Schulen): schulindividuelle Liste mit Adresse,
// Ansprechpartner und internen Notizen pflegen. Sehen dürfen sie alle User der Schule,
// bearbeiten Koordinator und Schul-Admin; Betrachter öffnen das Formular schreibgeschützt.
// UI adaptiert vom Praxen-Muster (Modal mit Fieldsets, Auswahl per Checkbox +
// "Auswahl löschen"). Die Liste speist das Dropdown am Schüler und die Zuordnung
// im CSV-Import.
let list: Kreisverband[] = [];
let currentEl: HTMLElement | null = null;

async function load() {
  list = await api.kreisverbaende.list();
}

// Global handler für onclick in den Tabellenzeilen
(window as any).__kvEdit = (id: number) => {
  const k = list.find(x => x.id === id);
  if (k) openForm(k);
};

function renderTable(el: HTMLElement) {
  const tbody = el.querySelector("tbody")!;
  if (list.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${canWrite() ? 6 : 5}" class="empty-state">Keine Kreisverbände vorhanden</td></tr>`;
    return;
  }
  const cell = (v: string | null, maxWidth: number) =>
    `<td data-full="${esc(v || "")}" class="text-muted" style="max-width:${maxWidth}px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(v || "")}</td>`;
  tbody.innerHTML = list.map(k => `
    <tr style="cursor:pointer" onclick="window.__kvEdit(${k.id})">
      ${writeOnly(`<td onclick="event.stopPropagation()"><input type="checkbox" class="row-sel" data-id="${k.id}" onchange="window.__kvUpdateSel()" /></td>`)}
      <td data-full="${esc(k.name)}" style="font-weight:500;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(k.name)}</td>
      ${cell(k.ort, 160)}
      ${cell(k.contactPerson, 200)}
      ${cell(k.contactPhone, 160)}
      ${cell(k.contactEmail, 220)}
    </tr>`).join("");
}

function openForm(k?: Kreisverband) {
  const isEdit = !!k;
  const v = (field: keyof KreisverbandInput) => esc(String(k?.[field] ?? ""));
  const title = !isEdit ? "Neuer Kreisverband" : canWrite() ? "Kreisverband bearbeiten" : "Kreisverband";
  openModal(title, `
    <fieldset><legend>Grunddaten</legend><div class="form-grid">
      <div class="form-group col-span-2"><label>Name *</label><input id="f-name" value="${v("name")}" /></div>
      <div class="form-group"><label>Straße</label><input id="f-street" value="${v("street")}" /></div>
      <div class="form-group"><label>Hausnr.</label><input id="f-hno" value="${v("houseNo")}" /></div>
      <div class="form-group"><label>PLZ</label><input id="f-plz" value="${v("plz")}" /></div>
      <div class="form-group"><label>Ort</label><input id="f-ort" value="${v("ort")}" /></div>
    </div></fieldset>
    <fieldset><legend>Ansprechpartner</legend><div class="form-grid">
      <div class="form-group col-span-2"><label>Name</label><input id="f-cp" value="${v("contactPerson")}" /></div>
      <div class="form-group"><label>Telefon</label><input id="f-cpp" type="tel" value="${v("contactPhone")}" /></div>
      <div class="form-group"><label>E-Mail</label><input id="f-cpe" type="email" value="${v("contactEmail")}" /></div>
    </div></fieldset>
    <fieldset><legend>Interne Notizen <span class="label-hint">(nur Schule)</span></legend><div class="form-grid">
      <div class="form-group col-span-2"><label>Notizen</label><textarea id="f-notes" rows="4">${v("notes")}</textarea></div>
    </div></fieldset>
    <div class="form-actions">
      <button class="btn btn-secondary" id="f-cancel">${canWrite() ? "Abbrechen" : "Schließen"}</button>
      ${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}
    </div>`, { wide: true, stickyActions: true });
  const mb = modalBody();
  // Betrachter sehen die Maske schreibgeschützt
  lockInputs(mb);
  mb.querySelector("#f-cancel")!.addEventListener("click", closeModal);
  const val = (sel: string) => (mb.querySelector(sel) as HTMLInputElement).value.trim();
  mb.querySelector("#f-save")?.addEventListener("click", async () => {
    const data = {
      name: val("#f-name"),
      street: val("#f-street"), houseNo: val("#f-hno"), plz: val("#f-plz"), ort: val("#f-ort"),
      contactPerson: val("#f-cp"), contactPhone: val("#f-cpp"), contactEmail: val("#f-cpe"),
      notes: val("#f-notes"),
    };
    if (!data.name) { (mb.querySelector("#f-name") as HTMLInputElement).focus(); return; }
    try {
      if (isEdit) await api.kreisverbaende.update(k!.id, data);
      else await api.kreisverbaende.create(data);
      closeModal();
      await load();
      if (currentEl) renderTable(currentEl);
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
  if (canWrite()) (mb.querySelector("#f-name") as HTMLInputElement).focus();
}

export async function render(el: HTMLElement) {
  currentEl = el;
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Kreisverbände</h1>
        <p style="color:var(--gray-600);font-size:.875rem;margin:.25rem 0 0">${canWrite()
          ? "Pflege hier die Kreisverbände deiner Schule mit Adresse und Ansprechpartner. Sie stehen anschließend als Auswahl am Schüler und im CSV-Import zur Verfügung."
          : "Die Kreisverbände deiner Schule mit Adresse und Ansprechpartner. Sie stehen als Auswahl am Schüler und im CSV-Import zur Verfügung."}</p>
      </div>
      ${writeOnly(`<button class="btn btn-primary" id="btn-add" style="white-space:nowrap">+ Neuer Kreisverband</button>`)}
    </div>
    ${readOnlyNotice()}
    ${writeOnly(`<div class="filter-bar"><button class="btn btn-danger btn-sm" id="btn-del-sel-kv" disabled>Auswahl löschen</button></div>`)}
    <div class="card table-wrap"><table><thead><tr>${writeOnly(`<th style="width:36px"><input type="checkbox" id="sel-all-kv" style="width:16px;height:16px;accent-color:var(--primary);cursor:pointer" /></th>`)}<th>Name</th><th>Ort</th><th>Ansprechpartner</th><th>Telefon</th><th>E-Mail</th></tr></thead><tbody></tbody></table></div>`;

  el.querySelector("#btn-add")?.addEventListener("click", () => openForm());

  try {
    await load();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }
  renderTable(el);

  // Auswahl-Checkbox + Bulk-Delete Wiring (Header ist statisch, daher nur einmal in render())
  const updateSelKv = () => {
    const boxes = [...el.querySelectorAll<HTMLInputElement>(".row-sel")];
    const checked = boxes.filter(b => b.checked);
    const selAll = el.querySelector<HTMLInputElement>("#sel-all-kv");
    const delBtn = el.querySelector<HTMLButtonElement>("#btn-del-sel-kv");
    if (selAll) { selAll.checked = checked.length === boxes.length && boxes.length > 0; selAll.indeterminate = checked.length > 0 && checked.length < boxes.length; }
    if (delBtn) delBtn.disabled = checked.length === 0;
  };
  (window as any).__kvUpdateSel = updateSelKv;
  el.querySelector<HTMLInputElement>("#sel-all-kv")?.addEventListener("change", (e) => {
    const cb = e.target as HTMLInputElement;
    el.querySelectorAll<HTMLInputElement>(".row-sel").forEach(b => b.checked = cb.checked);
    updateSelKv();
  });
  el.querySelector<HTMLButtonElement>("#btn-del-sel-kv")?.addEventListener("click", async () => {
    const ids = [...el.querySelectorAll<HTMLInputElement>(".row-sel:checked")].map(b => +b.dataset.id!);
    if (!ids.length) return;
    if (!confirm(`${ids.length} Kreisverband${ids.length !== 1 ? "e" : ""} löschen? Zugeordnete Schüler verlieren die Zuordnung.`)) return;
    try {
      await Promise.all(ids.map(id => api.kreisverbaende.remove(id)));
      await load();
      renderTable(el);
      updateSelKv();
    } catch (err: any) { alert("Fehler: " + err.message); }
  });
}
